import React from "react";
import { Check, ArrowRight, Settings, Zap, Shield, Activity } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const technicalServices = [{
  id: "item-1",
  title: "Electrical Panel Upgrades",
  icon: Zap,
  specs: ["Modern 200-Amp Service Modernization", "Enhanced Safety & Code Compliance", "Increased Property Resale Value", "Texas Storm Peace of Mind", "Elimination of Flickering & Breaker Trips", "24/7 Emergency Support Integration"],
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_panel_upgrade.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_breaker_box.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_electrical_safety.png"]
}, {
  id: "item-2",
  title: "Standby Generator Installation",
  icon: Activity,
  specs: ["Whole-Home Backup Power Reliability", "Automatic Power Transfer Technology", "Peace of Mind During Texas Outages", "Safety-First Fuel System Interlocks", "Increased Home Resale Value", "Professional Load Balancing & Testing"],
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_generator_install.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_standby_power.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_transfer_switch.png"]
}, {
  id: "item-3",
  title: "Whole-Home Surge Protection",
  icon: Shield,
  specs: ["Point-of-Entry Voltage Regulation", "Safety for Sensitive Electronics", "Texas Lightning Strike Mitigation", "Peace of Mind for Home Wiring", "Insurance Premium Reduction Potential", "Long-Term Appliance Resale Protection"],
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_surge_protection.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_lightning_shield.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_power_safety.png"]
}, {
  id: "item-4",
  title: "Level 2 EV Charger Installation",
  icon: Settings,
  specs: ["Rapid Level 2 Home Charging Setup", "Safety-Certified Station Installation", "Dedicated 240V Circuit Integration", "Peace of Mind with Expert Wiring", "Future-Proof Home Resale Value", "Full Permit & Inspection Management"],
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_ev_charger_install.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_tesla_wall.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_garage_charging.png"]
}];
export default function TechnicalSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="60551" id="essential-power-solutions" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        {}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(#2563EB 1px, transparent 1px), linear-gradient(90deg, #2563EB 1px, transparent 1px)`,
        backgroundSize: "40px 40px"
      }} />
        {}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#2563EB] opacity-[0.08] blur-[120px] rounded-full translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#2563EB] opacity-[0.05] blur-[100px] rounded-full -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-[#0F172A]">
            Essential Power Solutions
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            As a family-owned and operated business since 2013, Humberg Electrical Services handles everything from small repairs to massive infrastructure projects. No job is too big or too small for our expert Richmond team.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {technicalServices.map((service, index) => <AccordionItem key={service.id} value={service.id} className="border border-border bg-card/30 backdrop-blur-sm px-6 rounded-lg data-[state=open]:bg-card/50 data-[state=open]:border-[#2563EB]/50 transition-all duration-300" data-index={index}>
                <AccordionTrigger className="hover:no-underline py-6 group">
                  <div className="flex items-center gap-4 text-left">
                    <div className="p-2 rounded-md bg-secondary/50 text-[#2563EB] group-hover:text-white group-hover:bg-[#2563EB] transition-colors duration-300">
                      <service.icon className="size-6" />
                    </div>
                    <span className="text-lg md:text-xl font-semibold tracking-tight group-hover:text-[#2563EB] transition-colors text-[#0F172A]">
                      {service.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4 pb-8">
                    {}
                    <div className="space-y-6">
                      <div className="bg-muted border-l-4 border-[#EF4444]">
                        <h4 className="text-sm font-mono uppercase tracking-wider text-[#0F172A] mb-4 border-b border-border pb-2">
                          Why Upgrade & Benefits
                        </h4>
                        <ul className="space-y-3">
                          {service.specs.map((spec, i) => <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground/90" data-index={i}>
                              <Check className="size-5 text-[#EF4444] shrink-0 mt-0.5" />
                              <span>{spec}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div className="pt-4">
                        <Button asChild variant="outline" className="group border-[#2563EB] text-[#2563EB] hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444]">
                          <Link to="/ContactPage">
                            Request Consultation
                            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </Button>
                      </div>
                    </div>

                    {}
                    <div className="relative bg-black/5 rounded-lg p-1 border border-border/50">
                      <div className="absolute top-3 left-4 z-20 bg-[#0F172A]/80 backdrop-blur-md px-2 py-1 rounded text-xs font-mono text-white border border-white/10">
                        PROJECT GALLERY
                      </div>
                      <Carousel className="w-full">
                        <CarouselContent>
                          {service.images.map((img, imgIndex) => <CarouselItem key={imgIndex} data-index={imgIndex}>
                              <div className="aspect-video relative overflow-hidden rounded-md bg-muted">
                                <img src={img} alt={`${service.title} project example ${imgIndex + 1}`} className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" />
                              </div>
                            </CarouselItem>)}
                        </CarouselContent>
                        <div className="flex justify-end gap-2 mt-2 pr-2">
                          <CarouselPrevious className="static translate-y-0 translate-x-0 size-8 border-border bg-background/80 hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444]" />
                          <CarouselNext className="static translate-y-0 translate-x-0 size-8 border-border bg-background/80 hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444]" />
                        </div>
                      </Carousel>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}